import { FC, ReactNode, useEffect, useState } from 'react';

import { ApplicationProvider, I18n } from '@eneco/common/application';
import { ErrorBoundary } from '@eneco/common/log';
import { SWRCacheProvider } from '@eneco/common/swr';
import { UTMCapture } from '@eneco/components/UTMCapture';
import { LinkComponentProvider } from '@eneco/link';
import { MockConfigProvider } from '@eneco/mocks/worker';
import { Head, Link, useRouter } from '@eneco/next';
import { NbaDataProvider, type NbaDataType } from '@eneco/next-best-action';
import { OverloadGuard } from '@eneco/overload';
import { SparkyProvider } from '@eneco/sparky/providers';
import { GTMProvider, TrackingProvider } from '@eneco/tracking';
import { fetchPlaceholder } from '@sitecore/client/browser';
import {
  ComponentsDefinition,
  PlaceholderProvider,
  SitecoreContext,
  VisitorIdentification,
} from '@sitecore/common';
import type { LayoutServiceData, LayoutServiceExperimentInfo } from '@sitecore/types/lib';
import { Session } from 'next-auth';

import { ErrorPageContainer } from './Error';

export type SitecoreRouteProps = {
  businessUnit: string;
  children: ReactNode;
  components: ComponentsDefinition;
  dataLayerEvents?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  i18n: I18n;
  idToken?: Session['idToken'] | null;
  label: string;
  layoutData: LayoutServiceData;
  name: string;
  nbaData?: NbaDataType;
  path: string;
  platformHint?: 'desktop' | 'mobile';
  user?: Session['user'] | null;
};

const SitecoreRouteContainer: FC<SitecoreRouteProps> = ({
  businessUnit,
  children,
  components,
  dataLayerEvents,
  i18n,
  idToken,
  label,
  layoutData,
  name,
  nbaData,
  path,
  platformHint,
}) => {
  const route = layoutData?.sitecore?.route;
  const browserTitleValue = route?.fields?.browserTitle?.value;
  const pageTitleValue = route?.fields?.pageTitle?.value;

  // Ensure we only use string values for the title
  const title =
    (typeof browserTitleValue === 'string' ? browserTitleValue : null) ||
    (typeof pageTitleValue === 'string' ? pageTitleValue : null) ||
    label;
  const theme = route?.fields?.sparkyThemeOption?.value?.toString() ?? 'default';
  const metaDescription = route?.fields?.metaDescription?.value;
  const metaKeywords = route?.fields?.metaKeywords?.value;
  const [experimentInfo, setExperimentInfo] = useState<LayoutServiceExperimentInfo | undefined>(undefined);
  const sc_experimentInfo = layoutData?.sitecore?.context?.experimentInfo;

  const { asPath } = useRouter();

  function getIndexFollowStatus(): string {
    const { doNotFollow, doNotIndex } = route?.fields ?? {};
    const followStatus = doNotFollow?.value ? 'nofollow' : 'follow';
    const indexStatus = doNotIndex?.value ? 'noindex' : 'index';
    return `${indexStatus}, ${followStatus}`;
  }

  // Make the experimentInfo available on the global object for debugging,
  // and cache it so we always have the last defined version.
  useEffect(() => {
    if (sc_experimentInfo) {
      setExperimentInfo(sc_experimentInfo);
    }
    (window as any).experimentInfo = sc_experimentInfo || experimentInfo; // eslint-disable-line @typescript-eslint/no-explicit-any
  }, [sc_experimentInfo, experimentInfo]);

  return (
    <ErrorBoundary
      fallback={
        <ErrorPageContainer
          businessUnit={businessUnit}
          components={components}
          i18n={i18n}
          label={label}
          layoutData={null}
          name={name}>
          {children}
        </ErrorPageContainer>
      }>
      <Head>
        <title>{title}</title>
        {<meta name="robots" content={getIndexFollowStatus()} />}
        {metaDescription && <meta name="description" content={String(metaDescription)} />}
        {metaKeywords && <meta name="keywords" content={String(metaKeywords)} />}
      </Head>
      <MockConfigProvider>
        <ApplicationProvider i18n={i18n} path={path} isEditMode={!!layoutData?.sitecore?.context?.pageEditing}>
          <NbaDataProvider data={nbaData}>
            <GTMProvider
              eventsFromSSR={dataLayerEvents}
              experimentInfo={experimentInfo}
              customerId={layoutData.sitecore.context?.customerId}
              accountId={layoutData.sitecore.context?.accountId}
              businessUnit={businessUnit}
              label={label}
              useLegacy={businessUnit !== 'nl'}>
              <TrackingProvider scope={name}>
                <SparkyProvider locale={i18n.locale} platformHint={platformHint} theme={theme}>
                  <SitecoreContext components={components} layoutData={layoutData}>
                    <PlaceholderProvider
                      fetchPlaceholder={fetchPlaceholder}
                      idToken={idToken}
                      currentRoutePath={asPath}>
                      <UTMCapture />
                      <VisitorIdentification defer />
                      <SWRCacheProvider>
                        <OverloadGuard>
                          <LinkComponentProvider component={Link}>{children}</LinkComponentProvider>
                        </OverloadGuard>
                      </SWRCacheProvider>
                    </PlaceholderProvider>
                  </SitecoreContext>
                </SparkyProvider>
              </TrackingProvider>
            </GTMProvider>
          </NbaDataProvider>
        </ApplicationProvider>
      </MockConfigProvider>
    </ErrorBoundary>
  );
};

export { SitecoreRouteContainer };
